/**
 * star-class.ts — v4M6.1 derivation helper for `StarSystem.starClass`.
 *
 * **v4M0.7 supersedes the v4M6.1 "purely cosmetic" framing.** Classes
 * now carry two mechanical axes per `docs/MAP.md §2.6`:
 *
 *   1. **Resource yield modifier** — `STAR_CLASS_YIELD` multiplier
 *      applied to per-pop credits / research / minerals / fuel
 *      generation in `advance.ts` step 3a (population delta is NOT
 *      modified; habitability still drives growth/decline).
 *   2. **Habitability biome bias** — per-class biome distribution
 *      weighting at scenario gen via `STAR_CLASS_BIOME_BIAS`. Affects
 *      `scenario.ts` planet-biome roll only; post-gen planet state is
 *      unaffected.
 *
 * The class itself stays derived (no save-format bump): `STAR_CLASS_YIELD`
 * is a static constant; `STAR_CLASS_BIOME_BIAS` is consumed at gen-time
 * only. Same system → same class across save / reload, and that same
 * system's per-tick income just folds the yield multiplier each frame.
 *
 * Distribution per MAP.md §2.3:
 *   - red-dwarf  — ~50%
 *   - yellow     — ~35%
 *   - blue-giant — ~15%
 *
 * Pattern note: because the hash takes only `systemId`, every save's
 * "system 1" gets the same class, "system 2" the same class, etc. The
 * pattern is invisible in normal play (the player doesn't track system
 * ids) but stable for testing / debugging.
 */

import type { BiomeKind, StarClass } from './types/index';

/**
 * Stateless [0, 1) hash. Splitmix32 variant — fast, well-distributed,
 * deterministic across V8 / SpiderMonkey / WebKit. Used here only for
 * cosmetic-class derivation, so cryptographic strength isn't required.
 */
function hash01(n: number): number {
  let x = (n + 0x6d2b79f5) | 0;
  x = Math.imul(x ^ (x >>> 15), x | 1);
  x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
  return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
}

/**
 * v4M6.1 — derive a system's visual star class from its id. Pure,
 * deterministic, fast. Same value every call for the same id.
 *
 * Distribution buckets per MAP.md §2.3:
 *   - h < 0.50           → 'red-dwarf'
 *   - 0.50 ≤ h < 0.85    → 'yellow'
 *   - h ≥ 0.85           → 'blue-giant'
 */
export function deriveStarClass(systemId: number): StarClass {
  const h = hash01(systemId);
  if (h < 0.5) return 'red-dwarf';
  if (h < 0.85) return 'yellow';
  return 'blue-giant';
}

/**
 * v4M0.7 — per-class resource yield modifier (`docs/MAP.md §2.6.1`).
 * Multiplied on top of all existing per-planet economy multipliers
 * (Trade Center, Processing Plant, Research Lab, Refinery) and
 * habitability efficiency in `advance.ts` step 3a. Affects credits /
 * minerals / fuel / research per-pop generation. Population delta is
 * NOT modified — habitability still drives growth/decline.
 *
 * Tuning lives in this constant; future playtest can adjust values
 * without re-locking the design (per `MAP.md §2.6.5`).
 */
export const STAR_CLASS_YIELD: Record<StarClass, number> = {
  'red-dwarf': 0.85,
  yellow: 1.0,
  'blue-giant': 1.15,
};

/**
 * v4M10.4 — display labels for the detail-panel readout. Title-cased
 * human-readable form of each class id.
 */
export const STAR_CLASS_LABELS: Record<StarClass, string> = {
  'red-dwarf': 'Red Dwarf',
  yellow: 'Yellow Main-Sequence',
  'blue-giant': 'Blue Giant',
};

/**
 * v4M0.7 — per-class biome distribution weighting (`docs/MAP.md §2.6.1`).
 * Applied at scenario generation only — biases the per-planet biome
 * roll toward each class's preferred biomes. Post-gen `Planet.biome`
 * is unaffected.
 *
 * Values are multiplicative weights on the per-biome probability.
 * Biomes not listed default to `1.0`. **Gaia is exempt** from the
 * weighting and retains its uniform rare-roll probability per
 * `PLANETS.md` regardless of class.
 */
export const STAR_CLASS_BIOME_BIAS: Record<StarClass, Partial<Record<BiomeKind, number>>> = {
  'red-dwarf': {
    // Cold biomes preferred.
    arctic: 2.0,
    temperate: 1.5,
  },
  yellow: {
    // Temperate biomes preferred.
    temperate: 2.0,
    ocean: 1.5,
  },
  'blue-giant': {
    // Hot biomes preferred.
    volcanic: 2.0,
    desert: 1.5,
    barren: 1.5,
  },
};
